/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Volume2, Maximize, Minimize, X, Clock } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useBrand } from '@/lib/whitelabel';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import { THEME_CONFIG } from '@/config/themeConfig';

/**
 * KioskPage — waiting-room mode. Full-screen, high-contrast display that
 * shows the current / next patient scheduled for the logged-in professional.
 *
 * Designed for a dedicated device (tablet on a stand) in the waiting area.
 * Polls the appointments API every 20 seconds. Optional TTS announcement
 * ("Sig. Rossi Mario, prego") using the browser's SpeechSynthesis API when
 * the announced patient changes.
 */
export default function KioskPage() {
  const brand = useBrand();
  const { user } = useAuth() || {};
  const navigate = useNavigate();
  const [now, setNow] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [announcedId, setAnnouncedId] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [ttsEnabled, setTtsEnabled] = useState(true);

  // Clock tick every 5s
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 5_000);
    return () => clearInterval(t);
  }, []);

  // Poll appointments every 20s
  useEffect(() => {
    let cancelled = false;
    const fetchAppts = async () => {
      try {
        const res = await api.get('/telemedicine/appointments?upcoming=1&limit=5');
        if (cancelled) return;
        const raw = res.data;
        const list = Array.isArray(raw) ? raw
                    : (Array.isArray(raw?.items) ? raw.items
                    : (Array.isArray(raw?.appointments) ? raw.appointments : []));
        setAppointments(list);
      } catch { /* silent */ }
    };
    fetchAppts();
    const t = setInterval(fetchAppts, 20_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const { current, next } = useMemo(() => {
    const list = [...(appointments || [])].sort((a, b) => new Date(a.slot_at || a.scheduled_at || 0) - new Date(b.slot_at || b.scheduled_at || 0));
    const currentTime = now.getTime();
    // "Current" = closest appointment within 15 min (before/after)
    let cur = null;
    let nxt = null;
    for (const a of list) {
      const t = new Date(a.slot_at).getTime();
      const delta = t - currentTime;
      if (delta < 15 * 60_000 && delta > -30 * 60_000 && !cur) {
        cur = a;
      } else if (delta > 0 && !nxt) {
        nxt = a;
      }
    }
    return { current: cur, next: nxt };
  }, [appointments, now]);

  // TTS announcement when "current" patient changes.
  useEffect(() => {
    if (!ttsEnabled || !current || current.id === announcedId) return;
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
    try {
      const patientName = current.patient_name || current.patient?.full_name || 'paziente';
      const utterance = new SpeechSynthesisUtterance(`${patientName}, prego, si accomodi.`);
      utterance.lang = 'it-IT';
      utterance.rate = 0.85;
      utterance.volume = 1.0;
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(utterance);
      setAnnouncedId(current.id);
    } catch (e) { /* ignore */ }
  }, [current, announcedId, ttsEnabled]);

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch { /* ignore */ }
  };

  const announceAgain = () => {
    if (!current) return;
    setAnnouncedId(null);
    // Force re-announcement on next tick
  };

  const doctorName = user?.full_name || brand.clinicName;
  const timeStr = now.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });

  return (
    <div
      className="fixed inset-0 flex flex-col text-white overflow-hidden"
      style={{
        background: `linear-gradient(135deg, ${brand.primaryColor || THEME_CONFIG.colors.primary} 0%, ${brand.primaryColorAlt || THEME_CONFIG.colors.secondary} 100%)`,
      }}
      data-testid="kiosk-page"
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-8 py-4 bg-black/20 backdrop-blur-sm">
        <div className="flex items-center gap-3">
          {brand.logoUrl && <img src={brand.logoUrl} alt={brand.clinicName} className="h-12 w-12 rounded-lg object-cover" />}
          <div>
            <div className="text-2xl font-bold" data-testid="kiosk-clinic-name">{brand.clinicName}</div>
            <div className="text-sm opacity-80">{doctorName}</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-3xl font-mono tabular-nums" data-testid="kiosk-clock">
            <Clock className="inline h-6 w-6 mr-2 -mt-1" />
            {timeStr}
          </div>
          <Button variant="ghost" size="icon" onClick={() => setTtsEnabled((v) => !v)} aria-label="Toggle audio" className="text-white hover:bg-white/10" data-testid="kiosk-tts-toggle">
            <Volume2 className={`h-6 w-6 ${ttsEnabled ? '' : 'opacity-40'}`} />
          </Button>
          <Button variant="ghost" size="icon" onClick={toggleFullscreen} aria-label="Fullscreen" className="text-white hover:bg-white/10" data-testid="kiosk-fullscreen">
            {isFullscreen ? <Minimize className="h-6 w-6" /> : <Maximize className="h-6 w-6" />}
          </Button>
          <Button variant="ghost" size="icon" onClick={() => navigate('/feed')} aria-label="Esci" className="text-white hover:bg-white/10" data-testid="kiosk-exit">
            <X className="h-6 w-6" />
          </Button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col items-center justify-center px-8 gap-8 text-center">
        {current ? (
          <motion.div
            key={current.id}
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="space-y-4"
            data-testid="kiosk-current-patient"
          >
            <div className="text-2xl uppercase tracking-widest opacity-80">Prego, si accomodi</div>
            <div className="text-8xl md:text-9xl font-black tracking-tight" style={{ textShadow: '0 4px 24px rgba(0,0,0,0.3)' }}>
              {current.patient_name || current.patient?.full_name || 'Paziente'}
            </div>
            <div className="text-3xl opacity-80">
              {new Date(current.slot_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
              {current.type && <span className="ml-3">• {current.type}</span>}
            </div>
            <Button size="lg" variant="secondary" onClick={announceAgain} data-testid="kiosk-announce-btn" className="mt-4">
              <Volume2 className="h-5 w-5 mr-2" /> Chiama di nuovo
            </Button>
          </motion.div>
        ) : (
          <div className="space-y-3" data-testid="kiosk-empty">
            <div className="text-4xl opacity-80">Nessun paziente in attesa</div>
            <div className="text-xl opacity-60">
              {next
                ? `Prossimo appuntamento: ${new Date(next.slot_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`
                : 'Nessun appuntamento programmato'}
            </div>
          </div>
        )}

        {next && current && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="mt-6 px-8 py-4 rounded-2xl bg-black/20 backdrop-blur-sm max-w-xl w-full"
            data-testid="kiosk-next-patient"
          >
            <div className="text-sm uppercase tracking-widest opacity-70">Il prossimo</div>
            <div className="text-3xl font-bold mt-1">
              {next.patient_name || next.patient?.full_name || 'Paziente'}
            </div>
            <div className="text-lg opacity-70 mt-1">
              {new Date(next.slot_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
            </div>
          </motion.div>
        )}
      </div>

      {/* Footer tagline */}
      <div className="px-8 py-4 bg-black/10 text-center text-sm opacity-60">
        {brand.tagline || 'La salute al centro'}
      </div>
    </div>
  );
}
